import { useState, useEffect, useRef, useCallback } from 'react';
import CONFIG from '../config';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  loadFontResultsMeta,
  loadSlideFontResult,
  subscribeToFontAnalysisProgress
} from '../services/fontResultService';
import { fetchWithAuth } from '../utils/api';
import { downloadFile } from '../utils/downloadUtils';
import { loadFontDraft, deleteFontDraft } from '../services/draftService';

export const useFontAnalysis = (slides, selectedPpt, userId, fonts, showAlertModal, showConfirmModal, setIsModifying, onAnalysisComplete, t) => {
  // 전체 슬라이드 폰트 검사 관련 상태
  const [allSlidesFontResults, setAllSlidesFontResults] = useState([]);
  // Draft 존재 여부
  const [hasFontDraft, setHasFontDraft] = useState(false);
  // 로드된 Draft 데이터 (모달 열 때 확인 후 적용)
  const [loadedFontDraft, setLoadedFontDraft] = useState(null);
  // [{slide_index, violations_count, has_violations, thumbnail_url}, ...]
  const [fontCheckStatus, setFontCheckStatus] = useState(''); // 'checking', 'completed', 'no_violations', 'error'
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [checkProgress, setCheckProgress] = useState({ status: '', percent: 0, completed: false });

  // 슬라이드 크기 정보
  const [slideSize, setSlideSize] = useState({ width: 0, height: 0 });

  // 총 위반 개수 (메타데이터에서 로드)
  const [totalViolations, setTotalViolations] = useState(0);

  // 결과 로딩 상태
  const [isLoadingResults, setIsLoadingResults] = useState(false);

  // 리스너 참조 저장 (탭 전환/PPT 전환 시 복구용)
  const unsubscribeRef = useRef(null);
  // 콜백 ref (의존성 루프 방지)
  const callbacksRef = useRef({ showAlertModal, showConfirmModal, onAnalysisComplete, t });
  callbacksRef.current = { showAlertModal, showConfirmModal, onAnalysisComplete, t };

  // 배열 비교 유틸리티 함수
  const arraysEqual = (a, b) => {
    if (a.length !== b.length) return false;
    return a.every((val, index) => val === b[index]);
  };

  // 진행상황 업데이트 핸들러
  const handleProgressUpdate = useCallback(async (progressData) => {
    const { showAlertModal, showConfirmModal, onAnalysisComplete, t } = callbacksRef.current;

    // 에러 발생 시
    if (progressData.error) {
      console.log('[useFontAnalysis] 검사 에러:', progressData.error);

      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      setIsAnalyzing(false);
      setFontCheckStatus('error');
      setCheckProgress({ status: '', percent: 0, completed: false });

      showAlertModal(t('hooks.font.errorDuringCheck', { error: progressData.error }), 'error');
      return;
    }

    // 진행상황 업데이트
    setCheckProgress({
      status: progressData.status || '',
      percent: progressData.percent || 0,
      completed: progressData.completed || false
    });

    // 완료 시 결과 로드 + 모달 표시
    if (progressData.completed) {
      console.log('[useFontAnalysis] 검사 완료, 결과 로드 중...');

      // 리스너 해제
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      try {
        // 메타데이터만 로드 (개별 슬라이드는 모달에서 lazy loading)
        const metaData = await loadFontResultsMeta(selectedPpt);

        if (metaData) {
          // 슬라이드 결과는 빈 배열로 시작 (모달에서 lazy loading)
          setAllSlidesFontResults([]);

          if (metaData.slide_width && metaData.slide_height) {
            setSlideSize({
              width: metaData.slide_width,
              height: metaData.slide_height
            });
          }

          // 총 위반 개수 설정
          const violations = metaData.total_violations || 0;
          setTotalViolations(violations);

          const hasViolations = violations > 0;
          setFontCheckStatus(hasViolations ? 'completed' : 'no_violations');

          // 모달 표시 전에 스피너 중지
          setIsAnalyzing(false);

          // 완료 모달 표시 (비동기 처리)
          showConfirmModal(
            t('hooks.font.checkComplete', { count: violations })
          ).then(async (confirmed) => {
            if (confirmed && onAnalysisComplete) {
              // 첫 번째 슬라이드 결과 미리 로드 (모달에서 바로 보이도록)
              try {
                const firstSlideResult = await loadSlideFontResult(selectedPpt, 0);
                if (firstSlideResult) {
                  setAllSlidesFontResults([firstSlideResult]);
                }
              } catch (loadError) {
                console.error('[useFontAnalysis] 첫 번째 슬라이드 로드 오류:', loadError);
              }
              onAnalysisComplete('font');
            }
          });
        } else {
          setIsAnalyzing(false);
        }
      } catch (error) {
        console.error('[useFontAnalysis] 결과 로드 오류:', error);
        setFontCheckStatus('error');
        setIsAnalyzing(false);
      }
    }
  }, [selectedPpt]);

  // PPT 변경 시 상태 초기화, 기존 결과 로드, 진행 중인 검사 복구
  useEffect(() => {
    const loadExistingResults = async () => {
      // 기존 리스너 해제
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      if (!selectedPpt || !userId) {
        setAllSlidesFontResults([]);
        setFontCheckStatus('');
        setCheckProgress({ status: '', percent: 0, completed: false });
        setIsAnalyzing(false);
        return;
      }

      try {
        setIsLoadingResults(true);

        // 1. 진행 중인 검사 확인
        const progressRef = doc(db, 'PPT_FILE', selectedPpt, 'font_results', 'analysis_progress');
        const progressSnap = await getDoc(progressRef);

        if (progressSnap.exists()) {
          const progressData = progressSnap.data();

          // 진행 중인 검사가 있는 경우 (완료되지 않고, 에러도 없는 경우)
          if (!progressData.completed && !progressData.error && progressData.status) {
            console.log('[useFontAnalysis] 진행 중인 폰트 검사 발견, 리스너 연결');

            setIsAnalyzing(true);
            setFontCheckStatus('checking');
            setCheckProgress({
              status: progressData.status || '',
              percent: progressData.percent || 0,
              completed: false
            });

            // 리스너 시작
            unsubscribeRef.current = subscribeToFontAnalysisProgress(selectedPpt, handleProgressUpdate);
            setIsLoadingResults(false);
            return;
          }
        }

        // 2. 메타데이터만 로드 (lazy loading - 개별 슬라이드는 모달에서 로드)
        const metaData = await loadFontResultsMeta(selectedPpt);

        if (metaData && metaData.total_slides > 0) {
          // 슬라이드 결과는 빈 배열로 시작 (모달에서 lazy loading)
          setAllSlidesFontResults([]);

          if (metaData.slide_width && metaData.slide_height) {
            setSlideSize({
              width: metaData.slide_width,
              height: metaData.slide_height
            });
          }

          // 총 위반 개수 설정
          setTotalViolations(metaData.total_violations || 0);

          // 브랜드키트 폰트가 변경되었는지 확인
          const storedFonts = metaData.brand_fonts || [];
          const currentFonts = fonts || [];

          const fontsChanged = !arraysEqual(
            storedFonts.map(f => f.trim().toLowerCase()).sort(),
            currentFonts.map(f => f.trim().toLowerCase()).sort()
          );

          if (fontsChanged) {
            setFontCheckStatus('outdated');
          } else {
            // total_violations로 상태 결정 (개별 슬라이드 로드 없이)
            const hasViolations = metaData.total_violations > 0;
            setFontCheckStatus(hasViolations ? 'completed' : 'no_violations');
          }

          // 3. Draft 로드 (검사 결과가 있는 경우에만) - 바로 적용하지 않고 저장만
          const draft = await loadFontDraft(selectedPpt, userId);
          if (draft && Object.keys(draft).length > 0) {
            setHasFontDraft(true);
            setLoadedFontDraft(draft);
            console.log('[useFontAnalysis] 폰트 draft 로드됨:', Object.keys(draft).length, '개 슬라이드');
          } else {
            setHasFontDraft(false);
            setLoadedFontDraft(null);
          }
        } else {
          setAllSlidesFontResults([]);
          setFontCheckStatus('');
          setTotalViolations(0);
          setHasFontDraft(false);
          setLoadedFontDraft(null);
        }
      } catch (error) {
        console.error('[useFontAnalysis] 기존 결과 로드 오류:', error);
        setAllSlidesFontResults([]);
        setFontCheckStatus('');
        setHasFontDraft(false);
        setLoadedFontDraft(null);
      } finally {
        setIsLoadingResults(false);
      }
    };

    loadExistingResults();

    // 컴포넌트 언마운트 시 리스너 해제
    return () => {
      if (unsubscribeRef.current) {
        console.log('[useFontAnalysis] 리스너 해제 (언마운트)');
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [selectedPpt, userId, fonts, handleProgressUpdate]);

  // 전체 슬라이드 폰트 분석 함수 (비동기 방식)
  const analyzeAllSlidesFonts = async () => {
    console.log('[DEBUG] analyzeAllSlidesFonts 함수 호출됨');

    if (!selectedPpt || !userId) {
      showAlertModal(t('hooks.font.noDocOrUser'), 'error');
      return;
    }

    if (!fonts || fonts.length === 0) {
      showAlertModal(t('hooks.font.noBrandkitFonts'), 'warning');
      return;
    }

    if (!slides || slides.length === 0) {
      showAlertModal(t('hooks.font.noSlides'), 'warning');
      return;
    }

    setIsAnalyzing(true);
    setFontCheckStatus('checking');
    setCheckProgress({ status: 'preparing', percent: 0, completed: false });

    // 새로운 분석 시작 시 기존 draft 삭제
    await deleteFontDraft(selectedPpt);
    setHasFontDraft(false);

    try {
      // 기존 리스너 해제
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }

      // API 호출 (즉시 응답)
      const response = await fetchWithAuth(`${CONFIG.CLOUD_FUNCTION_URL}/check_all_slides_fonts`, {
        method: 'POST',
        body: JSON.stringify({
          doc_id: selectedPpt,
          userId: userId,
          brand_fonts: fonts
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      // 즉시 응답 수신 - 백그라운드 처리 시작됨
      if (data.success) {
        console.log('[useFontAnalysis] 폰트 검사 시작됨:', data);

        // 리스너 시작 (완료까지 유지)
        unsubscribeRef.current = subscribeToFontAnalysisProgress(selectedPpt, handleProgressUpdate);
      }

    } catch (error) {
      console.error('폰트 검사 시작 오류:', error);

      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      setFontCheckStatus('error');
      setCheckProgress({ status: '', percent: 0, completed: false });
      setIsAnalyzing(false);

      showAlertModal(error.message || t('hooks.font.errorGeneric'), 'error');
    }
  };

  // 결과 썸네일 클릭 시 모달 열기 (Color와 동일한 패턴)
  const handleResultThumbnailClick = (slideIndex, onModalOpen) => {
    if (onModalOpen) {
      onModalOpen(slideIndex);
    }
  };

  // 현재 선택된 슬라이드의 검사 결과 가져오기 (Color와 동일)
  const getCurrentSlideResult = (slideIndex) => {
    return allSlidesFontResults.find(result => result.slide_index === slideIndex);
  };

  // 특정 슬라이드 결과 lazy loading (결과가 없으면 로드)
  const loadSlideResultIfNeeded = useCallback(async (slideIndex) => {
    // 이미 로드된 경우 스킵
    const existing = allSlidesFontResults.find(r => r.slide_index === slideIndex);
    if (existing) return existing;

    if (!selectedPpt) return null;

    try {
      const result = await loadSlideFontResult(selectedPpt, slideIndex);
      if (result) {
        setAllSlidesFontResults(prev => {
          // 중복 체크
          if (prev.find(r => r.slide_index === slideIndex)) return prev;
          return [...prev, result];
        });
        return result;
      }
      return null;
    } catch (error) {
      console.error(`[useFontAnalysis] 슬라이드 ${slideIndex} 결과 로드 오류:`, error);
      return null;
    }
  }, [selectedPpt, allSlidesFontResults]);

  // PPT 수정하기 함수 (모달에서 호출)
  const handlePPTModification = async (modalSlideIndex, violationFontChanges, isMultiSlide = false) => {
    // isMultiSlide가 true면 violationFontChanges는 { slideIndex: { violationIndex: font } } 구조
    // isMultiSlide가 false면 기존과 동일 { violationIndex: font } 구조

    let modifications = [];

    if (isMultiSlide) {
      // 여러 슬라이드 처리
      Object.entries(violationFontChanges).forEach(([slideIndex, slideChanges]) => {
        Object.entries(slideChanges).forEach(([violationIndex, newFont]) => {
          // 빈 값 검증
          if (!newFont || newFont.trim() === '') {
            return; // 빈 값은 스킵
          }

          modifications.push({
            slide_index: parseInt(slideIndex),
            violation_index: parseInt(violationIndex),
            new_font: newFont.trim()
          });
        });
      });
    } else {
      // 단일 슬라이드 처리 (기존 로직)
      // 유효한 변경사항이 있는지 확인
      const hasValidChanges = violationFontChanges && Object.entries(violationFontChanges).some(([_key, value]) => value && value.trim() !== '');

      if (!hasValidChanges) {
        showAlertModal(t('hooks.font.setFontFirst'), 'warning');
        return;
      }

      modifications = Object.entries(violationFontChanges).map(([violationIndex, newFont]) => ({
        slide_index: modalSlideIndex,
        violation_index: parseInt(violationIndex),
        new_font: newFont.trim()
      }));
    }

    if (modifications.length === 0) {
      showAlertModal(t('hooks.font.noFontToModify'), 'warning');
      return;
    }

    // selectedPpt는 이미 docId
    try {
      // 로딩 상태 시작
      if (setIsModifying) setIsModifying(true);

      const requestData = {
        doc_id: selectedPpt,
        userId: userId,
        modifications: modifications
      };

      const response = await fetchWithAuth(`${CONFIG.CLOUD_FUNCTION_URL}/modify_slide_fonts`, {
        method: 'POST',
        body: JSON.stringify(requestData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('hooks.font.modifyError'));
      }

      // 성공 메시지
      const slideCount = isMultiSlide
        ? Object.keys(violationFontChanges).length
        : 1;
      const message = t('hooks.font.modifySuccess', { slideCount, count: modifications.length });

      // 성공 시 다운로드 링크 제공
      if (data.download_url) {
        const downloadConfirm = await showConfirmModal(message);
        if (downloadConfirm) {
          // Blob 방식으로 다운로드 (Edge 호환)
          await downloadFile(data.download_url, data.filename);
        }
      } else {
        showAlertModal(data.message || t('hooks.font.modifyComplete'), 'success');
      }

      return true; // 성공 시 true 반환

    } catch (error) {
      console.error('PPT 수정 오류:', error);
      showAlertModal(error.message || t('hooks.font.modifyError'), 'error');
      return false; // 실패 시 false 반환
    } finally {
      // 로딩 상태 종료
      if (setIsModifying) setIsModifying(false);
    }
  };

  // Draft 적용 함수 (모달에서 확인 시 호출)
  const applyFontDraft = useCallback(() => {
    if (loadedFontDraft) {
      console.log('[useFontAnalysis] 폰트 draft 적용됨');
      return loadedFontDraft;
    }
    return null;
  }, [loadedFontDraft]);

  // Draft 초기화 함수 (모달에서 취소 시 호출)
  const clearFontDraft = useCallback(() => {
    setLoadedFontDraft(null);
    setHasFontDraft(false);
    console.log('[useFontAnalysis] 폰트 draft 초기화됨');
  }, []);

  return {
    allSlidesFontResults,
    fontCheckStatus,
    isAnalyzing,
    checkProgress,
    slideSize,
    totalViolations,
    isLoadingResults,
    hasFontDraft,
    loadedFontDraft,
    analyzeAllSlidesFonts,
    handleResultThumbnailClick,
    getCurrentSlideResult,
    loadSlideResultIfNeeded,
    handlePPTModification,
    applyFontDraft,
    clearFontDraft
  };
};
